import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import {
  FaBuilding,
  FaChartBar,
  FaCoins,
  FaNetworkWired,
  FaPlus,
  FaUsers
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import countryPriceApi from "../api/countryPriceApi";
import franchiseAdminAPI from "../api/franchiseAdminAPI"; // new import for franchise count
import frymesOrderApi from "../api/frymeOrderApi";
import usersApi from "../api/userApi";
import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const [totalFranchises, setTotalFranchises] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalCreditsSold, setTotalCreditsSold] = useState(0);
  const [activeCountries, setActiveCountries] = useState(0);
  const [recentRequests, setRecentRequests] = useState([]);

  // Separate fetch functions for each KPI
  const fetchFranchises = async () => {
    try {
      const res = await franchiseAdminAPI.getCountOfFranchises();
      // API may return plain number or { data: number }
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setTotalFranchises(count);
    } catch (e) {
      console.error("Failed to fetch franchise count", e);
      toast.error("Failed to load franchise count");
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await usersApi.usersCount();
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setTotalUsers(count);
    } catch (e) {
      console.error("Failed to fetch user count", e);
      toast.error("Failed to load user count");
    }
  };

  const fetchCredits = async () => {
    try {
      const res = await frymesOrderApi.getCountOfSoldFrymesByMA();
      // Assuming response may be { data: { totalQuantity: number } } or plain number
      const count =
        typeof res === "number"
          ? res
          : (res?.data?.totalQuantity ?? res?.data ?? 0);
      setTotalCreditsSold(count);
    } catch (e) {
      console.error("Failed to fetch credits sold", e);
      toast.error("Failed to load credits sold");
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await countryPriceApi.getCountOfActiveCountries();
      const count = typeof res === "number" ? res : (res?.data ?? 0);
      setActiveCountries(count);
    } catch (e) {
      console.error("Failed to fetch active countries", e);
      toast.error("Failed to load active countries");
    }
  };

  const fetchRecent = async () => {
    try {
      const res = await frymesOrderApi.getLast5RequestsOfMA();
      // Expected shape: { data: { content: [] } } or plain array
      const list = Array.isArray(res)
        ? res
        : (res?.data?.content ?? res?.data ?? []);
      setRecentRequests(list);
    } catch (e) {
      console.error("Failed to fetch recent requests", e);
      toast.error("Failed to load recent activity");
    }
  };

  useEffect(() => {
    // Trigger all fetches (no need to wait for each other)
    fetchFranchises();
    fetchUsers();
    fetchCredits();
    fetchCountries();
    fetchRecent();
  }, []);

  // Helper to generate activity description based on request data

  return (
    <div
      className="container-fluid p-4 scrollable-container"
      style={{ height: "100%" }}
    >
      <ToastContainer position="top-right" autoClose={3000} />
      <h2 className="mb-4 fw-bold">Master Admin Dashboard</h2>

      {/* KPI Cards */}
      <Row className="g-4 mb-5">
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100 card-glass">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <FaBuilding size={40} className="text-primary mb-3" />
              <Card.Title className="text-muted">Total Franchises</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {totalFranchises}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100 card-glass">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <FaUsers size={40} className="text-success mb-3" />
              <Card.Title className="text-muted">Total Users</Card.Title>
              <Card.Text className="display-5 fw-bold">{totalUsers}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100 card-glass">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <FaCoins size={40} className="text-warning mb-3" />
              <Card.Title className="text-muted">Total Credits Sold</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {totalCreditsSold}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center shadow-sm border-0 h-100 card-glass">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              <FaNetworkWired size={40} className="text-info mb-3" />
              <Card.Title className="text-muted">Active Countries</Card.Title>
              <Card.Text className="display-5 fw-bold">
                {activeCountries}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Recent Activity Table */}
        <Col md={8}>
          <Card className="shadow-sm border-0 h-100 card-glass">
            <Card.Header className="bg-white border-0 pt-4 pb-0">
              <h5 className="fw-bold mb-0">Recent Activity</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive hover className="mt-3">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Activity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRequests.map((req, idx) => (
                    <tr key={idx}>
                      <td>{req.createdAt}</td>
                      <td>
                        {req.buyer ? (
                          <>
                            <strong>Franchise</strong>
                            <br />
                            <small>{req.buyer?.organization || "-"}</small>
                            <br />
                            <small>{req.buyer?.location || "-"}</small>
                            <br />
                            <small>{req.buyer?.email || "-"}</small>
                          </>
                        ) : (
                          <>
                            <strong>Customer</strong>
                            <br />
                            <small>{req.customerName || "Customer"}</small>
                            <br />

                            {/* Show company details if available */}
                            {req.companyDetails ? (
                              <>
                                <small>
                                  {req.companyDetails?.companyName || "-"}
                                </small>
                                <br />
                                <small>
                                  {req.companyDetails?.location || "-"}
                                </small>
                              </>
                            ) : (
                              <>
                                <small>{req.customerEmail || "-"}</small>
                                <small>{req.quantity || "-"}</small>
                              </>
                            )}
                          </>
                        )}
                      </td>

                      <td>
                        <Badge
                          bg={
                            req.status === "APPROVED"
                              ? "success"
                              : req.status === "REJECTED"
                                ? "danger"
                                : "warning"
                          }
                        >
                          {req.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {recentRequests.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center">
                        No recent activity
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Quick Actions */}
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100 bg-light card-glass">
            <Card.Header className="bg-transparent border-0 pt-4 pb-0">
              <h5 className="fw-bold mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-3 mt-3">
              <Button
                variant="primary"
                size="lg"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate("/master-admin-dashboard/franchises/add")}
              >
                <FaPlus /> Add New Franchise
              </Button>
              <Button
                variant="outline-success"
                size="lg"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={() => navigate("/master-admin-dashboard/frymes-management?tab=pendingRequests")}
              >
                <FaCoins /> Manage Credit Requests
              </Button>
              <Button
                variant="outline-info"
                size="lg"
                className="d-flex align-items-center justify-content-center gap-2"
                onClick={()=> navigate("/master-admin-dashboard/credit-management/add-credits")}
              >
                <FaChartBar />Manage country pricing
              </Button>
            
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
